import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { type UpdateCppPartnerBody, updateCppPartner } from '~/services/LuxLoyalty';

import { costPerPointQueryKeys } from './costPerPointQueryKeys';

interface UpdateCppPartnerVariables {
  partnerId: string;
  body: UpdateCppPartnerBody;
}

/**
 * Updates a partner's billing details.
 *
 * Invalidates the whole CPP tree rather than just the partner list: the invoice reads its Bill To
 * block and GST rate straight off the partner, so a saved change has to reach any invoice already
 * rendered for that partner, not only the directory it was edited from.
 */
export function useUpdateCppPartner() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ partnerId, body }: UpdateCppPartnerVariables) => {
      return await updateCppPartner(partnerId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: costPerPointQueryKeys.all });
      enqueueSnackbar('Partner updated', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(`Could not update partner: ${error instanceof Error ? error.message : 'unknown error'}`, {
        variant: 'error',
      });
    },
  });
}
